import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { Glyphs, logger } from '@/ui'
import {
	flip,
	FloatingPortal,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useTransitionStyles,
} from '@floating-ui/react'
import {
	campaignAclIndexQueryKey,
	campaignsIndexQueryKey,
	roleEnum,
	useCampaignAclDestroy,
	useCampaignAclIndex,
	useCampaignAclStore,
	useOrganisationMembershipsIndex,
	useOrganisationRelationShow,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserHoverCard } from './user-hover-card'

function displayName(user: { firstname: string | null; lastname: string | null; email: string }): string {
	if (user.firstname || user.lastname) {
		return [user.firstname, user.lastname].filter(Boolean).join(' ')
	}
	return user.email
}

type AccessorSummary = {
	id: string
	firstname: string | null
	lastname: string | null
	email: string
	role: string | null
}

export type CampaignAccessProps = {
	campaignId: string
	prefetch: boolean
	className?: string
	source: 'dashboard' | 'editor'
	accessors?: AccessorSummary[]
}

export function CampaignAccess({ campaignId, prefetch, className, source, accessors }: CampaignAccessProps) {
	const organisationId = useJobillaOrgId()
	const { user, can } = useAuth()
	const queryClient = useQueryClient()
	const { data: orgData } = useOrganisationRelationShow(organisationId)
	const aclEnabled = orgData?.accessMode === 'explicit'
	const canManage = can('campaign-access:update')
	const showAclList = aclEnabled || canManage
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)
	const [adminsExpanded, setAdminsExpanded] = useState(false)
	const { data: aclData, isLoading, isFetching } = useCampaignAclIndex(campaignId, { perPage: 100, page: 1 }, {
		enabled: showAclList && (isOpen || (prefetch && accessors == null)),
	})
	const invalidate = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: campaignsIndexQueryKey() }),
			queryClient.invalidateQueries({ queryKey: campaignAclIndexQueryKey(campaignId) }),
		])
	const grantAccess = useCampaignAclStore({ onSuccess: invalidate })
	const revokeAccess = useCampaignAclDestroy({ onSuccess: invalidate })
	const { data: membersData } = useOrganisationMembershipsIndex(organisationId, { perPage: 100, page: 1 }, {
		enabled: showAclList && (prefetch || isOpen),
	})
	const resolvedAccessors: AccessorSummary[] | null = aclData != null
		? aclData.data.filter((accessor) => accessor.role !== 'jobillan')
		: accessors ?? null
	const adminUsers = (resolvedAccessors ?? []).filter((accessor) => accessor.role === 'admin')
	const nonAdminUsers = (resolvedAccessors ?? []).filter((accessor) => accessor.role !== 'admin')
	const accessibleUserIds = new Set((resolvedAccessors ?? []).map((accessor) => accessor.id))
	const teamMembers = (membersData?.data ?? []).filter((member) => !accessibleUserIds.has(member.id))
	const pillAccessors = accessors ?? resolvedAccessors
	const namedAccessor = pillAccessors?.find((accessor) => accessor.firstname) ?? null
	const isMutating = grantAccess.isPending || revokeAccess.isPending

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom-end',
		middleware: [flip(), shift()],
	})

	const click = useClick(context)
	const dismiss = useDismiss(context)

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
	])

	const { isMounted, styles } = useTransitionStyles(context, {
		duration: 150,
	})

	if (!can('campaign-access:read') || !showAclList) {
		return null
	}

	return (
		<div className={className}>
			<button
				ref={refs.setReference}
				type="button"
				className="min-w-0 flex items-center gap-1 text-grey-400 leading-none rounded-full px-1.5 py-1 transition-colors hover:bg-grey-100 hover:text-grey-600 cursor-pointer"
				{...getReferenceProps({
					onClick(event) {
						event.preventDefault()
						event.stopPropagation()
						if (!isOpen) {
							logger.track('Campaign access: ACL viewed', {
								data: {
									source,
									campaign_id: campaignId,
								},
							})
						}
					},
				})}
			>
				<div className="size-4 shrink-0">
					<Glyphs.heroiconsMicro.UserIcon />
				</div>
				{isMutating
					? <div className="size-3 border border-grey-300 border-t-grey-500 rounded-full animate-spin" />
					: pillAccessors == null
					? isLoading || isFetching
						? <div className="size-3 border border-grey-300 border-t-grey-500 rounded-full animate-spin" />
						: null
					: !aclEnabled && pillAccessors.length === 0
					? (
						<span className="shrink-0 text-xs font-medium text-grey-600">
							{t('dashboard.access.all')}
						</span>
					)
					: namedAccessor
					? (
						<>
							<span className="max-w-20 truncate text-xs font-medium text-grey-600">
								{namedAccessor.firstname}
							</span>
							{pillAccessors.length > 1 && (
								<span className="shrink-0 text-xs font-medium text-grey-600">
									+ {pillAccessors.length - 1}
								</span>
							)}
						</>
					)
					: (
						<span className="max-w-12 truncate text-xs font-medium text-grey-600">
							{pillAccessors.length}
						</span>
					)}
			</button>
			{isMounted && (
				<FloatingPortal>
					<div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
						<div
							style={styles}
							className="mt-1 w-56 bg-white rounded-lg shadow-lg ring-1 ring-grey-200 overflow-hidden"
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
							}}
						>
							<div className="p-1">
								<div className="px-2 pt-1.5 pb-1 text-xs font-semibold text-grey-500">
									{t('dashboard.access.accessibleTo')}
								</div>
								{!aclEnabled && (
									<div className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-grey-400">
										<span>{t('dashboard.access.allAdminsAndMembers')}</span>
										<div className="size-3.5 text-grey-300 shrink-0">
											<Glyphs.heroiconsMicro.LockClosedIcon />
										</div>
									</div>
								)}
								{aclEnabled && adminUsers.length > 0 && (
									<>
										<button
											type="button"
											onClick={() => setAdminsExpanded(!adminsExpanded)}
											className="w-full flex items-center justify-between px-2 py-1.5 text-sm text-grey-400 hover:bg-grey-50 rounded cursor-pointer"
										>
											<span>{t('dashboard.access.allAdminUsers')}</span>
											<div
												className={`size-3 text-grey-400 transition-transform ${
													adminsExpanded ? 'rotate-180' : ''
												}`}
											>
												<Glyphs.micro.DownChevron />
											</div>
										</button>
										{adminsExpanded
											&& adminUsers.map((adminUser) => (
												<UserHoverCard key={adminUser.id} user={adminUser}>
													<div className="w-full flex items-center justify-between gap-2 px-2 py-1.5 pl-4 text-sm text-grey-400 min-w-0 cursor-help">
														<span className="truncate">
															{displayName(adminUser)}
														</span>
														<div className="size-3.5 text-grey-300 shrink-0">
															<Glyphs.heroiconsMicro.LockClosedIcon />
														</div>
													</div>
												</UserHoverCard>
											))}
									</>
								)}
								{nonAdminUsers.map((accessibleUser) => {
									const isSelf = accessibleUser.id === user?.id
									const removable = !isSelf
									const isRevoking = revokeAccess.isPending
										&& revokeAccess.variables?.user === accessibleUser.id

									return (
										<UserHoverCard key={accessibleUser.id} user={accessibleUser}>
											<button
												type="button"
												disabled={!removable || revokeAccess.isPending}
												onClick={() => {
													if (!removable) {
														return
													}

													logger.track('Campaign access: Revoked', {
														data: {
															source,
															campaign_id: campaignId,
															user_id: accessibleUser.id,
														},
													})

													revokeAccess.mutate({
														campaign: campaignId,
														user: accessibleUser.id,
													})
												}}
												className={`group/row w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded min-w-0 ${
													removable
														? 'text-grey-700 hover:bg-red-50 cursor-pointer'
														: 'text-grey-400 cursor-default'
												}`}
											>
												<span className="truncate">
													{displayName(accessibleUser)}
												</span>
												{isRevoking
													? (
														<div className="size-3 border border-red-300 border-t-red-500 rounded-full animate-spin shrink-0" />
													)
													: removable
													? (
														<div className="size-4 text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity">
															<Glyphs.heroiconsMicro.MinusCircleIcon />
														</div>
													)
													: (
														<div className="size-3.5 text-grey-300">
															<Glyphs.heroiconsMicro.LockClosedIcon />
														</div>
													)}
											</button>
										</UserHoverCard>
									)
								})}
							</div>
							{teamMembers.length > 0 && (
								<div className="border-t border-grey-100 p-1">
									<div className="px-2 pt-1.5 pb-1 text-xs font-semibold text-grey-500">
										{t('dashboard.access.teamMembers')}
									</div>
									{teamMembers.map((member) => {
										const isGranting = grantAccess.isPending
											&& grantAccess.variables?.user === member.id
										const hasImplicitAccess = !aclEnabled
											&& member.role !== roleEnum.recruiter

										return (
											<UserHoverCard key={member.id} user={member}>
												<button
													type="button"
													disabled={grantAccess.isPending || hasImplicitAccess}
													onClick={() => {
														if (hasImplicitAccess) {
															return
														}

														logger.track('Campaign access: Granted', {
															data: {
																source,
																campaign_id: campaignId,
																user_id: member.id,
															},
														})

														grantAccess.mutate({
															campaign: campaignId,
															user: member.id,
														})
													}}
													className={`group/row w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded min-w-0 disabled:opacity-50 ${
														hasImplicitAccess
															? 'text-grey-400 cursor-default'
															: 'text-grey-700 hover:bg-green-50 cursor-pointer'
													}`}
												>
													<span className="truncate">
														{displayName(member)}
													</span>
													{isGranting
														? (
															<div className="size-3 border border-green-300 border-t-green-500 rounded-full animate-spin shrink-0" />
														)
														: hasImplicitAccess
														? (
															<div className="size-3.5 text-grey-300 shrink-0">
																<Glyphs.heroiconsMicro.LockClosedIcon />
															</div>
														)
														: (
															<div className="size-4 text-green-500 opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0">
																<Glyphs.heroiconsMicro.PlusCircleIcon />
															</div>
														)}
												</button>
											</UserHoverCard>
										)
									})}
								</div>
							)}
						</div>
					</div>
				</FloatingPortal>
			)}
		</div>
	)
}
